// src/collab/CoOrganizersDialog.jsx
// Running a tournament together: the owner makes an invite link and sees (and can remove) who joined; a co-organizer
// sees who runs it with them and can leave. Needs a connection: it is the server that knows who is who.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import QrCode from '../components/QrCode';
import { useConfirm } from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';
import { displayNameOf } from '../auth';
import { publicOrigin } from '../native';
import { serverClient } from '../sync/sync';

export const inviteUrl = (token) => `${publicOrigin()}/join/${token}`;

function CoOrganizersDialog({ name, tournamentId, user, onClose, onLeft }) {
    const confirm = useConfirm();
    const toast = useToast();
    const server = serverClient(user.uid);
    const [info, setInfo] = useState(null); // null: loading
    const [failed, setFailed] = useState(false);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    const load = useCallback(async () => {
        try {
            setInfo(await serverClient(user.uid)('GET', `/tournaments/${tournamentId}/coorganizers`));
            setFailed(false);
        } catch (error) {
            setFailed(true);
        }
    }, [user.uid, tournamentId]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const act = async (description, action) => {
        setBusy(true);
        try {
            await action();
            await load();
        } catch (error) {
            toast.error(`Could not ${description}. ${error.message}`);
        }
        setBusy(false);
    };

    const createLink = () =>
        act('create the link', () =>
            server('POST', `/tournaments/${tournamentId}/coorganizers/invite`, { displayName: displayNameOf(user) })
        );

    const stopLink = async () => {
        const go = await confirm({
            title: 'Stop the invite link?',
            message: 'Nobody can join with it any more. People who already joined stay. You can make a new link later.',
            confirmLabel: 'Stop the link',
        });
        if (go) await act('stop the link', () => server('DELETE', `/tournaments/${tournamentId}/coorganizers/invite`));
    };

    const remove = async (member) => {
        const go = await confirm({
            title: `Remove ${member.name}?`,
            message: 'They can no longer see or change this tournament. It stays with you.',
            confirmLabel: 'Remove',
        });
        if (go) await act('remove them', () => server('DELETE', `/tournaments/${tournamentId}/coorganizers/${member.memberId}`));
    };

    const leave = async () => {
        const go = await confirm({
            title: 'Leave this tournament?',
            message: 'It disappears from your tournaments. The owner and the others keep running it.',
            confirmLabel: 'Leave',
        });
        if (!go) return;
        setBusy(true);
        try {
            await server('DELETE', `/tournaments/${tournamentId}/coorganizers/${user.uid}`);
            onLeft();
        } catch (error) {
            toast.error(`Could not leave. ${error.message}`);
            setBusy(false);
        }
    };

    const copy = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success('Link copied.');
        } catch (error) {
            inputRef.current?.select();
            toast.info('Press Ctrl+C (or copy) to copy the selected link.');
        }
    };

    const owner = info && info.role === 'owner';
    const url = info && info.inviteToken ? inviteUrl(info.inviteToken) : '';

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div
                className="modal share-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="coorg-title"
                onClick={(event) => event.stopPropagation()}
            >
                <h2 id="coorg-title">Co-organizers of {name}</h2>
                {failed ? (
                    <p className="form-error" role="alert">
                        This needs a connection to the server. Try again when you are online.
                    </p>
                ) : info === null ? (
                    <div className="skeleton" aria-busy="true" />
                ) : (
                    <>
                        <p className="muted">
                            {owner
                                ? 'People with the invite link can enter scores, add players, plan rounds and change settings, just like you. Only you can delete the tournament, manage this list and share the read-only link.'
                                : `You run this tournament together with ${info.owner.name}. You can do everything except delete it, manage co-organizers and share the read-only link.`}
                        </p>

                        {info.shared && (
                            <ul className="coorg-list" aria-label="People running this tournament">
                                <li>
                                    <span>
                                        <strong>{info.owner.name}</strong> <span className="muted">· owner</span>
                                    </span>
                                </li>
                                {info.members.map((member, index) => (
                                    <li key={member.memberId || index}>
                                        <span>
                                            <strong>{member.name}</strong>
                                            {member.you && <span className="muted"> · you</span>}
                                        </span>
                                        {owner && (
                                            <button className="btn btn-sm btn-ghost btn-danger-text" onClick={() => remove(member)} disabled={busy}>
                                                Remove
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}

                        {owner && (url ? (
                            <>
                                <div className="share-url">
                                    <input
                                        ref={inputRef}
                                        className="input"
                                        readOnly
                                        aria-label="Invite link"
                                        value={url}
                                        onFocus={(event) => event.target.select()}
                                    />
                                    <button className="btn btn-primary" onClick={() => copy(url)}>
                                        Copy link
                                    </button>
                                </div>
                                <div className="share-qr">
                                    <QrCode value={url} label={`QR code of the invite link for ${name}`} />
                                    <p className="hint">Whoever opens the link and signs in becomes a co-organizer.</p>
                                </div>
                                <div className="modal-actions share-actions">
                                    <button className="btn btn-ghost btn-danger-text" onClick={stopLink} disabled={busy}>
                                        Stop the link
                                    </button>
                                    <button className="btn" onClick={onClose}>
                                        Done
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="modal-actions">
                                <button className="btn" onClick={onClose}>
                                    Cancel
                                </button>
                                <button className="btn btn-primary" onClick={createLink} disabled={busy}>
                                    Create invite link
                                </button>
                            </div>
                        ))}

                        {!owner && (
                            <div className="modal-actions">
                                <button className="btn btn-ghost btn-danger-text" onClick={leave} disabled={busy}>
                                    Leave tournament
                                </button>
                                <button className="btn" onClick={onClose}>
                                    Done
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default CoOrganizersDialog;
